import { format } from 'date-fns';
import { vi } from 'date-fns/locale';

/**
 * Format blog post date in Vietnamese locale
 * @param dateString - ISO date string
 * @returns Formatted date string (e.g., "25 Tháng 11, 2025")
 */
export function formatBlogDate(dateString: string): string {
  const date = new Date(dateString);
  return format(date, 'dd MMMM, yyyy', { locale: vi });
}

/**
 * Get blog image URL with fallback
 * @param image - Image path or URL
 * @param type - Image type (blog, media, etc.)
 * @returns Full image URL
 */
export function getBlogImageUrl(image: string | undefined | null, type: string = 'blog'): string {
  if (!image) {
    return '/placeholder-blog.jpg'; // Fallback image
  }

  // If it's a full URL, return as is
  if (image.startsWith('http')) {
    return image;
  }

  // Otherwise, construct storage URL
  return `${import.meta.env.VITE_APP_URL}/assets/images/${type}/${image}`;
}

/**
 * Calculate reading time based on word count
 * @param content - HTML or plain text content
 * @returns Reading time in minutes
 */
export function getReadingTime(content: string): number {
  const wordsPerMinute = 200;
  // Remove HTML tags for more accurate word count
  const plainText = content.replace(/<[^>]*>/g, ' ');
  const wordCount = plainText.split(/\s+/).filter(word => word.length > 0).length;
  return Math.max(1, Math.ceil(wordCount / wordsPerMinute));
}

/**
 * Truncate text with ellipsis
 * @param text - Text to truncate
 * @param maxLength - Maximum length
 * @returns Truncated text
 */
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) {
    return text;
  }
  return text.substring(0, maxLength).trim() + '...';
}

/**
 * Generate share URLs for social media
 */
export function getShareUrls(url: string, title: string) {
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  return {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
  };
}

/**
 * Count active filters
 */
export function countActiveFilters(filters: { category?: string; tags?: string[] }): number {
  let count = 0;
  if (filters.category) count++;
  if (filters.tags && filters.tags.length > 0) count += filters.tags.length;
  return count;
}
